/**
 * SW2-2475 — which downloads the dashboard may remove from this device.
 *
 * The My Projects widget removed every local download that the server's list of the user's
 * downloads (`GET /api/offline/user/:id`) didn't mention, with no check for unsynced work. The list
 * can omit a download the device still needs:
 *
 *   - a projects administrator syncs the same project and clears the download for every user
 *     (`DELETE /api/offline/:projectId`), so the inspector's next dashboard load deleted their
 *     unsynced offline work;
 *   - another user logs in on a shared device: their list never names the first user's download;
 *   - the list is an old copy (the device's saved one, or the service worker's cache).
 *
 * So a download is removed only when the list is a fresh answer from the server, the download is
 * this user's (or predates `downloadedBy`, SW2-2459), and nothing of it is waiting to sync. One the
 * list omits but that still has work stays, and is shown so it can sync.
 */
import db, { countUnsyncedWork } from '../db/db';

const ownedBy = (project, userId) => project.downloadedBy == null || project.downloadedBy === userId;

/**
 * @param {any[]} localProjects  rows of db.projects
 * @param {Set<number>} listedIds  project ids the server's list names
 * @param {{ userId: number, fresh: boolean }} options  `fresh`: the list came from the server now
 * @returns {Promise<{ remove: any[], keepUnlisted: any[] }>}
 *   `remove` — safe to delete; `keepUnlisted` — not listed but kept (work waiting, or the list is
 *   not fresh): show it.
 */
export async function reconcileDownloads(localProjects, listedIds, { userId, fresh }) {
  const remove = [];
  const keepUnlisted = [];

  for (const project of localProjects) {
    if (listedIds.has(project.id) || !ownedBy(project, userId)) continue;

    let pending = true;

    try {
      pending = (await countUnsyncedWork(project.id)) > 0;
    } catch (err) {
      // Unreadable: treat as pending. A download is cheap to keep and impossible to recover.
    }

    // Not fresh: the list may simply be old, so the download stays and is shown.
    if (pending || !fresh) {
      keepUnlisted.push(project);
    } else {
      remove.push(project);
    }
  }

  return { remove, keepUnlisted };
}

/** This user's downloads on this device, for when the server's list can't be fetched at all. */
export const ownDownloads = (localProjects, userId) =>
  localProjects.filter((project) => ownedBy(project, userId));

/**
 * Which forms a download covers, for a download the server's list can't vouch for.
 *
 * `formsOnDevice` is recorded at download (InspectionsDue `onDownload`): the forms whose data was
 * stored. Inspection rows are not a record of that — the download fetches the project's latest
 * inspection whatever the form (PR #151 review). Downloads from before it was recorded fall back
 * to their inspections' forms, then to the whole project, as SW2-2297 treats an unattributable
 * download.
 */
export async function withLocalForms(project) {
  // Recorded (even empty: no form finished downloading) — the record is the answer.
  const recorded = Array.isArray(project.formsOnDevice);
  let templateIds = recorded ? project.formsOnDevice.map(Number).filter(Boolean) : [];
  let legacy = !recorded;

  if (legacy) {
    try {
      const inspections = await db.inspections.where('project_id').equals(project.id).toArray();

      templateIds = [...new Set(inspections.map((i) => Number(i.template_id)).filter(Boolean))];
    } catch (err) {
      // Unreadable: project-wide below.
    }
  }

  legacy = legacy && !templateIds.length;

  return {
    ...project,
    downloadedTemplateIds: templateIds,
    downloadedProjectWide: legacy,
  };
}

/**
 * Add the forms recorded on the device to a listed project. The list is the server's record of
 * what was downloaded, and it can name fewer forms than the device holds: a saved list may predate
 * a later download of another form, and an administrator clearing a download followed by a fresh
 * download of one form leaves the server naming only that form while the others, unsynced work and
 * all, are still here (PR #151 review). Only for this user's download — on a shared device the
 * recorded forms may be another user's.
 */
export const addFormsOnDevice = (entry, project, userId) =>
  ownedBy(project, userId)
    ? {
        ...entry,
        downloadedTemplateIds: [
          ...new Set([...(entry.downloadedTemplateIds || []), ...(project.formsOnDevice || []).map(Number)]),
        ],
      }
    : entry;

/**
 * The local downloads the server's list names, each covering the forms the list names plus those
 * recorded on the device — whether the list is fresh or saved.
 *
 * @param {any[]} localProjects  rows of db.projects
 * @param {Map<number, { templateIds: number[], projectWide: boolean }>} downloadedByProject
 * @param {number} userId
 */
export const listedDownloads = (localProjects, downloadedByProject, userId) =>
  localProjects
    .filter((project) => downloadedByProject.has(project.id))
    .map((project) => {
      const entry = downloadedByProject.get(project.id);

      return addFormsOnDevice(
        { ...project, downloadedTemplateIds: entry.templateIds, downloadedProjectWide: entry.projectWide },
        project,
        userId,
      );
    });
